import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Alert,
  Link,
} from '@mui/material';
import { GradientBackground, GlassmorphismCard, AnimatedLogo, BrandButton } from '../ui/enhanced';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState('');

  const { forgotPassword, error: authError, successMessage, loading, clearError, clearSuccessMessage } = useAuth();
  const navigate = useNavigate();

  const handleNavigateToLogin = () => {
    clearError();
    clearSuccessMessage();
    setLocalError('');
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setLocalError('Please enter your email address.');
      clearSuccessMessage();
      return;
    }

    try {
      setLocalError('');
      await forgotPassword(email);
    } catch (err: any) {
      setLocalError('An error occurred. Please try again.');
    }
  };

  const displayError = localError || authError;

  return (
    <GradientBackground variant="brand">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="xs">
          <GlassmorphismCard
            intensity="medium"
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              animation: 'fade-in 0.5s ease-in-out',
            }}
          >
            <AnimatedLogo
              src="/portal/OrangeLogo.png"
              alt="AskARC API Portal Logo"
              size={80}
              animation="float"
              sx={{ mb: 2 }}
            />
            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 1,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #753403 0%, #4F076F 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
              }}
            >
              Forgot Password
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mb: 3,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

          {displayError && (
            <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: '8px' }}>
              {displayError}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ width: '100%', mb: 2, borderRadius: '8px' }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px !important',
                },
              }}
            />

            <BrandButton
              type="submit"
              fullWidth
              variant="primary"
              loading={loading}
              disabled={!email || loading}
              sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: '8px' }}
            >
              Send Reset Link
            </BrandButton>
          </Box>

          <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={handleNavigateToLogin}
              sx={{
                color: '#753403',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              ← Back to Login
            </Link>
          </Box>
          </GlassmorphismCard>
        </Container>
      </Box>
    </GradientBackground>
  );
};

export default ForgotPassword;
